import React, { useMemo } from 'react'
import { useVideoConfig } from 'remotion'
import { AudiogramConfig, Orientation, SceneConfig } from '../types'
import useFonts from '../utils/useFonts'

type ResponsiveSceneProps = {
  audiogram: AudiogramConfig
  scene: SceneConfig
  orientation: Orientation
  u: (x?: number) => number
  h: (x?: number) => number
  w: (x?: number) => number
}
const ResponsiveSceneContext = React.createContext<ResponsiveSceneProps | null>(
  null,
)

export function useResponsiveSceneProps() {
  const context = React.useContext(ResponsiveSceneContext)
  if (context === null)
    throw new Error(
      'useResponsiveSceneProps() hook must be used within <ResponsiveSceneProvider>',
    )
  return context
}

export const ResponsiveSceneProvider = ({
  audiogram,
  children,
  scene,
  orientation,
}: {
  audiogram: AudiogramConfig
  children: React.ReactNode | React.ReactNode[]
  scene: SceneConfig
  orientation: Orientation
}) => {
  const { width, height } = useVideoConfig()
  const shortestDimension = width < height ? width : height
  const baseSize = 0.025 * shortestDimension

  const loaded = useFonts(audiogram.theme)

  const contextValue = useMemo(() => {
    const ctx: ResponsiveSceneProps = {
      audiogram,
      scene,
      orientation,
      u: (x = 1) => x * baseSize,
      w: (x = 1) => x * width,
      h: (x = 1) => x * height,
    }
    return ctx
  }, [audiogram, scene, orientation, baseSize, width, height])

  if (!loaded) return null

  return (
    <ResponsiveSceneContext.Provider value={contextValue}>
      <div style={{ userSelect: 'none' }}>{children}</div>
    </ResponsiveSceneContext.Provider>
  )
}
